import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImagePicker from "@/components/ImagePicker";

const PHOTO_SLOTS = [
    { key: "hero_image", caption_it: "hero_caption_it", caption_en: "hero_caption_en", label: "Hero (top della pagina)", hint: "Foto grande in alto. Consigliato: paesaggio orizzontale, 16:9 minimo 1600x900." },
    { key: "pool_image", caption_it: "pool_caption_it", caption_en: "pool_caption_en", label: "Foto piscina / area relax", hint: "Mostrata accanto all'intro Villa Smeraldo. 4:3 o 1:1 minimo 800x600." },
    { key: "breakfast_image", caption_it: "breakfast_caption_it", caption_en: "breakfast_caption_en", label: "Foto colazione / interni", hint: "Mostrata accanto all'intro. 4:3 o 1:1 minimo 800x600." },
    { key: "beach_image", caption_it: "beach_caption_it", caption_en: "beach_caption_en", label: "Foto RIVA Beach (sfondo)", hint: "Sfondo della sezione spiaggia. Tramonto sul mare o vista PSS. 16:9 minimo 1600x900." },
];

export default function AdminStay() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (user === null) return;
        if (!user || user.role !== "admin") {
            nav("/");
            return;
        }
        api.get("/site-content/stay")
            .then((res) => setData(res.data?.data || {}))
            .catch(() => setData({}))
            .finally(() => setLoading(false));
    }, [user, nav]);

    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/site-content/stay", { data });
            toast.success("Contenuti /stay salvati");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="max-w-3xl mx-auto px-5 py-20 text-zinc-400">Caricamento…</div>;
    }

    return (
        <div data-testid="admin-stay-page" className="max-w-4xl mx-auto px-5 md:px-10 py-14">
            <p className="text-xs tracking-[0.3em] uppercase text-abnormal-red font-bold mb-3 flex items-center gap-2">
                <ImageIcon size={14} /> ADMIN — STAY PAGE CONTENT
            </p>
            <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">Foto & Didascalie</h1>
            <p className="text-zinc-400 mt-4 max-w-2xl">
                Carica le foto reali della struttura per le 4 sezioni della pagina <code className="text-abnormal-red">/stay</code>.
                Per ogni foto puoi aggiungere una didascalia in italiano e una in inglese. Lascia vuoto per non mostrare didascalia.
            </p>

            <div className="mt-10 space-y-10">
                {PHOTO_SLOTS.map((slot) => (
                    <section key={slot.key} data-testid={`admin-stay-${slot.key}`} className="border border-zinc-800 bg-zinc-950 p-5 md:p-6">
                        <h2 className="font-display text-2xl uppercase">{slot.label}</h2>
                        <p className="text-xs text-zinc-500 mt-1">{slot.hint}</p>

                        <div className="mt-5">
                            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">Immagine</p>
                            <ImagePicker
                                value={data[slot.key] || ""}
                                onChange={(u) => set(slot.key, u)}
                                testidPrefix={`admin-stay-${slot.key}-img`}
                            />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 mt-5">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">Didascalia (IT)</p>
                                <input
                                    type="text"
                                    maxLength={200}
                                    placeholder="es. Piscina con vista Argentario al tramonto"
                                    value={data[slot.caption_it] || ""}
                                    onChange={(e) => set(slot.caption_it, e.target.value)}
                                    data-testid={`admin-stay-${slot.key}-cap-it`}
                                    className="w-full bg-black border border-zinc-800 focus:border-abnormal-red outline-none px-3 py-2 text-sm"
                                />
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">Caption (EN)</p>
                                <input
                                    type="text"
                                    maxLength={200}
                                    placeholder="e.g. Pool with Argentario sunset view"
                                    value={data[slot.caption_en] || ""}
                                    onChange={(e) => set(slot.caption_en, e.target.value)}
                                    data-testid={`admin-stay-${slot.key}-cap-en`}
                                    className="w-full bg-black border border-zinc-800 focus:border-abnormal-red outline-none px-3 py-2 text-sm"
                                />
                            </div>
                        </div>
                    </section>
                ))}
            </div>

            <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="admin-stay-save"
                className="mt-10 w-full bg-abnormal-red text-white uppercase tracking-[0.2em] font-bold py-4 hover:bg-white hover:text-abnormal-red transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
                <Save size={16} /> {saving ? "Salvo…" : "Salva tutto"}
            </button>

            <p className="mt-6 text-xs text-zinc-500 text-center">
                Le modifiche sono live immediatamente su <code className="text-abnormal-red">/stay</code> (puoi aprirla in un'altra tab e refreshare).
            </p>
        </div>
    );
}
